/**
 * Filter Serialization Utilities
 * Handles serialization/deserialization of filter state to/from localStorage
 * Includes date sanitization and filter validation
 */

import { LoadFiltersState, LocationData } from '../types/filters'
import { DHD_LIMITS } from './constants'
import { formatDateYMD, getToday } from './date'
import { safeStorageOperation, STORAGE_KEYS } from './localStorage'
import { readPerWindow, writePerWindow } from './perWindowStorage'

/** Interface for serialized filter state (dates as ISO strings) */
interface SerializedFilterState extends Omit<LoadFiltersState, 'origin' | 'destination' | 'startDate' | 'endDate'> {
    origin: LocationData | null
    destination: LocationData | null
    startDate: string | null
    endDate: string | null
}

/** Parse a YYYY-MM-DD date string as local midnight (avoids UTC timezone shift) */
const parseDateString = (dateStr: string): Date => {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) {
        return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]), 0, 0, 0, 0)
    }
    // Fallback for legacy ISO strings stored before this fix
    const date = new Date(dateStr)
    date.setHours(0, 0, 0, 0)
    return date
}

/** Ensure a date is not in the past (replaces with today if needed) */
const sanitizeDate = (date: Date | null): Date => {
    if (!date) return getToday()
    const today = getToday()
    const dateToCheck = new Date(date)
    // Check if date is invalid
    if (isNaN(dateToCheck.getTime())) {
        return today
    }
    dateToCheck.setHours(0, 0, 0, 0)
    // If date is before today, replace with today
    return dateToCheck < today ? today : dateToCheck
}

/** Default deadhead value for filters */
const DEFAULT_DEADHEAD = DHD_LIMITS.DEFAULT.toString()

/** Ensure deadhead value is valid — preserves empty string (user cleared the field) */
const ensureDeadheadValue = (value?: string | null): string => {
    if (value === null || value === undefined) {
        return DEFAULT_DEADHEAD
    }
    return value
}

/**
 * Save filters to localStorage with serialization
 * Dates are converted to ISO strings for storage
 */
export const saveFiltersToLocalStorage = (tabFilters: Record<string, LoadFiltersState>) => {
    safeStorageOperation(
        () => {
            const serialized: Record<string, SerializedFilterState> = {}

            for (const tabId in tabFilters) {
                const filters = tabFilters[tabId]
                serialized[tabId] = {
                    ...filters,
                    origin: filters.origin,
                    destination: filters.destination,
                    startDate: filters.startDate ? formatDateYMD(filters.startDate) : null,
                    endDate: filters.endDate ? formatDateYMD(filters.endDate) : null,
                }
            }
            writePerWindow(STORAGE_KEYS.FILTERS, JSON.stringify(serialized))
        },
        undefined,
        'saveFiltersToLocalStorage'
    )
}

/**
 * Load filters from localStorage with deserialization and date sanitization
 * Past dates are replaced with today's date
 */
export const loadFiltersFromLocalStorage = (): Record<string, LoadFiltersState> | null => {
    return safeStorageOperation(
        () => {
            const stored = readPerWindow(STORAGE_KEYS.FILTERS)
            if (!stored) return null

            const parsed = JSON.parse(stored) as Record<string, SerializedFilterState>
            const deserialized: Record<string, LoadFiltersState> = {}

            for (const tabId in parsed) {
                const filters = parsed[tabId]
                // Sanitize dates: replace past dates with today, keep today or future dates
                // Always ensure dates are valid Date objects, never null
                const startDate = filters.startDate ? sanitizeDate(parseDateString(filters.startDate)) : getToday()
                const endDate = filters.endDate ? sanitizeDate(parseDateString(filters.endDate)) : getToday()

                deserialized[tabId] = {
                    ...filters,
                    originDhd: ensureDeadheadValue(filters.originDhd),
                    destinationDhd: ensureDeadheadValue(filters.destinationDhd),
                    origin: filters.origin,
                    destination: filters.destination,
                    startDate,
                    endDate,
                }
            }

            return deserialized
        },
        null,
        'loadFiltersFromLocalStorage'
    )
}
